import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { subscribeToRoom } from '@db/roomService';
import { submitAnswer } from '@db/submissionService';
import { getCurrentUid, ensureAnonymousAuth } from '@db/firebase';
import type { WordCloudConfig } from '@types-local/quiz.types';
import type { Room } from '@types-local/room.types';

export default function AnswerWordCloud(): React.ReactElement {
  const [room, setRoom] = useState<Room | null>(null);
  const [words, setWords] = useState<string[]>(['']);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const studentName = sessionStorage.getItem('gh_student_name') ?? 'Student';
  const roomCode = sessionStorage.getItem('gh_room_code') ?? '';
  const classId = sessionStorage.getItem('gh_class_id') ?? '';

  useEffect(() => {
    ensureAnonymousAuth();
    if (!roomCode) return;
    return subscribeToRoom(roomCode, setRoom);
  }, [roomCode]);

  const config = room?.activeConfig?.type === 'word_cloud'
    ? (room.activeConfig as WordCloudConfig) : null;

  // Reset for each new prompt so students flow through the session.
  useEffect(() => {
    setWords(['']);
    setSubmitted(false);
  }, [config?.prompt]);

  if (!config) {
    return (
      <div className="min-h-screen flex items-center justify-center text-center p-6">
        <div className="space-y-3"><div className="text-5xl">☁️</div><h2 className="text-xl font-bold text-slate-900">Waiting for activity…</h2></div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center text-center p-6">
        <div className="space-y-4">
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="text-6xl">✅</motion.div>
          <h2 className="text-xl font-bold text-slate-900">Words Submitted!</h2>
        </div>
      </div>
    );
  }

  const max = config.maxWordsPerStudent;
  const setWordAt = (i: number, v: string) =>
    setWords((prev) => prev.map((w, idx) => (idx === i ? v : w)));

  const handleSubmit = async () => {
    const cleaned = words.map((w) => w.trim()).filter(Boolean);
    if (cleaned.length === 0 || !room?.activeSlideId) return;
    setSubmitting(true);
    try {
      const uid = getCurrentUid() ?? 'anon';
      await submitAnswer(roomCode, uid, studentName, room.activeSlideId, {
        type: 'word_cloud', words: cleaned,
      }, classId);
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col p-4 max-w-lg mx-auto">
      <div className="glass rounded-3xl p-5 mb-4">
        <p className="text-xs text-orange-400 font-semibold mb-2">☁️ Word Cloud</p>
        <h2 className="text-base font-bold text-slate-900">{config.prompt}</h2>
      </div>

      <div className="space-y-2 mb-4">
        {words.map((w, i) => (
          <input
            key={i}
            value={w}
            onChange={(e) => setWordAt(i, e.target.value)}
            placeholder={`Word ${i + 1}`}
            maxLength={30}
            className="w-full bg-surface-elevated border border-surface-border rounded-2xl px-4 py-3 text-base text-slate-900 placeholder-slate-400 focus:outline-none focus:border-orange-500 transition-colors"
          />
        ))}
        {words.length < max && (
          <button
            onClick={() => setWords((prev) => [...prev, ''])}
            className="text-xs text-orange-400 hover:text-orange-300 transition-colors pl-1"
          >
            + Add another word ({words.length}/{max})
          </button>
        )}
      </div>

      <motion.button
        onClick={handleSubmit}
        disabled={submitting || words.every((w) => !w.trim())}
        whileTap={{ scale: 0.97 }}
        className="w-full py-4 rounded-2xl bg-gradient-to-r from-orange-600 to-orange-500 text-white font-bold text-base disabled:opacity-60"
      >
        {submitting ? 'Submitting…' : '🚀 Submit'}
      </motion.button>
    </div>
  );
}
